import { useEffect, useState, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import TimeFilter from "../components/TimeFilter";
import MonthRangePicker from "../components/MonthRangePicker";
import StatCard from "../components/StatCard";
import DashboardSection from "../components/DashboardSection";
import ErrorState from "../components/ErrorState";
import DonutCard from "../components/charts/DonutCard";
import { SkeletonStatGrid, SkeletonChart } from "../components/Skeleton";
import { UsersIcon, CheckCircleIcon, AlertIcon } from "../components/icons";

import {
  usersToday,
  usersYesterday,
  usersLast7,
  usersLast30,
  usersMonthlyRange
} from "../api/user.api";

const STATUS_KEYS = ["Approved", "Pending", "Rejected", "Locked"];

export default function Users() {
  const [filter, setFilter] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (filter !== "custom") loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      let res;
      if (filter === "today") res = await usersToday();
      else if (filter === "yesterday") res = await usersYesterday();
      else if (filter === "last7") res = await usersLast7();
      else if (filter === "last30") res = await usersLast30();
      setData(res.data);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const loadCustom = async ({ fromYear, fromMonth, toYear, toMonth }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await usersMonthlyRange({
        from_year: fromYear,
        from_month: fromMonth,
        to_year: toYear,
        to_month: toMonth
      });
      setData(res.data.summary || res.data);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const statusData = useMemo(() => {
    if (!data || typeof data !== "object") return null;
    return STATUS_KEYS.filter(
      (k) => typeof data[k] === "number" && !Number.isNaN(data[k]) && data[k] > 0
    ).map((k) => ({
      label: k,
      value: data[k]
    }));
  }, [data]);

  return (
    <div className="page-container">
      <PageHeader
        title="Users & Growth"
        subtitle="Signup and activity counts across user onboarding and verification stages."
      />

      <div className="toolbar">
        <TimeFilter value={filter} onChange={setFilter} />
      </div>
      <MonthRangePicker open={filter === "custom"} onApply={loadCustom} />

      {error ? (
        <ErrorState message={error} onRetry={filter === "custom" ? undefined : loadData} />
      ) : (
        <>
          <DashboardSection title="Overview Statistics">
            {loading || !data ? (
              <SkeletonStatGrid count={4} />
            ) : (
              <div className="grid">
                {Object.entries(data)
                  .filter(([_, v]) => typeof v === "number" || typeof v === "string")
                  .map(([k, v], i) => (
                    <StatCard
                      key={k}
                      title={k.replace(/_/g, " ")}
                      value={v}
                      icon={UsersIcon}
                      index={i}
                    />
                  ))}
              </div>
            )}
          </DashboardSection>

          {loading ? (
            <SkeletonChart className="chart-card--block" />
          ) : (
            statusData &&
            statusData.length > 0 && (
              <DonutCard
                className="chart-card--block"
                title="Verification Status Breakdown"
                subtitle="Share of active users by verification state"
                data={statusData}
                centerLabel="users"
              />
            )
          )}
        </>
      )}
    </div>
  );
}
